import React, { useCallback, useState } from "react";
import moment from "moment";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import LocalPhoneTwoToneIcon from "@mui/icons-material/LocalPhoneTwoTone";
import EmailTwoToneIcon from "@mui/icons-material/EmailTwoTone";
import CakeTwoToneIcon from "@mui/icons-material/CakeTwoTone";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useQuery } from "@tanstack/react-query";
import { Alert, Chip, Grid, Typography } from "@mui/material";
import ImageRepository from "../../../Repositories/ImageRepository";
import Functions from "../../../Helpers/Functions";
import { fetchImageProfile, userTeachrSetAccountAction } from "../../../Redux/Actions/UserActions";
import Teachr from "../../../Objects/models/Teachr";
import { capitalizeFirstLetter, formatPhoneNumber } from "../../../Utils/Function";
import Teachr_Repository from "../../../Repositories/Teachr_Repository";
import { ProfileTeachr } from "../../../Objects/models/TeachrUpdateRCS";
import validateDescription from "../../../Validation/Description";
import { Subject } from "../../../Objects/models/Subject";
import { ENV_DECLARONS, ENV_VERIFIED_TRAINER, getActivePrestationBrandConfig, isBrandActive } from "../../../config/environment.config";
import FormInput from "../../../Components/Ui/FormInput";
import FormSubjectSelect from "../../../Components/FormFields/FormSubjectSelect";
import { useSettingsContext } from "../../../Contexts/SettingsContext";
import PhotoSection from "../../../Components/PhotoSection";
import SettingsSection from "../../../Components/SettingsSection";
import Settings, { useSettingsStyle } from "../../../Components/Settings";
import ProfileHeaderV2 from "../../SettingsV2/ProfileHeaderV2";
import Image from "../../../Objects/models/Image";
import { Address } from "../../../Objects/models/Address";
import AddressRepository from "../../../Repositories/AddressRepository";
import InputAddressField from "../../../Components/Ui/InputAddressField";
import { useTailwindBrand } from "../../../Components/shared";

import { addressFromGeocode } from '../../../Utils/googleAddress';
const ProfilStep = () => {
  const { teachr, dispatch, dataSponsor } = useSettingsContext()
  const { brandConfig } = useTailwindBrand();

  const [retry, setReTry] = useState(false);
  const { data: subject, refetch } = useQuery({
    queryKey: ['getSubjectsGivenByTeachr ', teachr?.id],
    queryFn: async () => {
      if (!teachr?.id) return null;
      const response = await Teachr_Repository.getSubjectsGivenByTeachr(teachr.id);
      return response;
    },
    retryOnMount: retry,
  })

  const [profileLoading, setProfileLoading] = useState(false);
  const [courses, setCourses] = useState<Subject[]>(subject ?? []);
  const getCourses = useCallback((value: Subject[]) => setCourses(value), []);
  const [isEditing, setIsEditing] = useState((subject !== undefined && courses?.length === 0) && !isBrandActive(ENV_VERIFIED_TRAINER) ? true : false)

  const [errorMsg, setErrorMsg] = useState({
    description: "",
    courses: ""
  });
  const [load, setLoad] = useState(false);
  const [image, setImage] = useState<Image>();
  const [erroImage, setErrorImage] = useState("");
  const [description, setDescription] = useState(teachr?.description ?? null)
  const [pendingAddress, setPendingAddress] = useState<Address | null>(null);
  const [addressError, setAddressError] = useState(false);

  const existingAddress: Address | null = teachr?.address ?? null;
  const displayAddress: Address | null = pendingAddress ?? existingAddress;
  const formattedAddress = displayAddress
    ? displayAddress.formattedAddress ||
    `${displayAddress.streetNumber} ${displayAddress.streetName}, ${displayAddress.city}`
    : null;
  const currentFormatted = existingAddress
    ? existingAddress.formattedAddress ||
    `${existingAddress.streetNumber} ${existingAddress.streetName}, ${existingAddress.city}`
    : undefined;

  const getFile = (value: File | null, error: boolean) => {
    if (error || !value) {
      setErrorImage(error ? "Mauvais format de fichier" : "");
      return;
    }
    setErrorImage("");
    const fileType = value.type.split("/");
    Functions.resizeFile(value, true, fileType[1]).then((response: any) => {
      if (URL.createObjectURL(response) && teachr) {
        setProfileLoading(true);
        ImageRepository.createImage(response)
          .then((imageResponse) => {
            setImage(imageResponse);
            // L'avatar (page profil + AppBar) lit teachr.profilePicture.contentUrl
            // depuis Redux : on y pousse tout de suite la nouvelle image pour que
            // l'affichage se mette à jour sans recharger la page.
            const updatedTeachr = JSON.parse(JSON.stringify(teachr));
            updatedTeachr.profilePicture = JSON.parse(JSON.stringify(imageResponse));
            dispatch(userTeachrSetAccountAction(updatedTeachr));
            dispatch({ type: "SET_TEACHR_INFO", payload: { teachr: updatedTeachr } });
            if (imageResponse.id) {
              dispatch(fetchImageProfile(imageResponse.id));
            }
          })
          .catch((error) => {
            console.error(
              "Error updating teachr:",
              error,
              JSON.parse(JSON.stringify(error))
            );
            setErrorImage("L'envoi de la photo a échoué, veuillez réessayer.");
          })
          .finally(() => {
            setProfileLoading(false);
          });
      }
    });
  };

  const getAddress = useCallback((placeId: string) => {
    geocodeByPlaceId(placeId)
      .then((response) => {
        const addressObj = addressFromGeocode(response[0], "", true);
        addressObj.sessionToken = "";
        setPendingAddress(addressObj);
        setAddressError(false);
      })
      .catch(() => {
        setAddressError(true);
      });
  }, []);

  const validate = (): boolean => {
    let valid = {
      description: false,
      courses: false
    }

    if (!validateDescription(description).isValid) {
      valid.description = true
    } else {
      valid.description = false
    }
    if (!isBrandActive(ENV_VERIFIED_TRAINER)) {
      if (courses.length === 0) {
        if (subject && subject.length > 0) {
          valid.courses = false
        } else {
          valid.courses = true
        }
      } else (
        valid.courses = false
      )
    }
    // setError(valid);

    setErrorMsg({
      description: valid.description ? validateDescription(description).message : "",
      courses: valid.courses ? `Veuillez renseigner vos ${getActivePrestationBrandConfig("s")}` : ``
    });

    return !valid.description && !valid.courses ? false : true
  }
  const updateTeachr = async () => {
    if (!teachr.id) return;
    if (!validate()) {
      setLoad(true)
      try {
        const clonedTeachr: Teachr = structuredClone ? structuredClone(teachr) : JSON.parse(JSON.stringify(teachr));

        if (description) {
          clonedTeachr.description = description
        }
        if (courses.length > 0) {
          clonedTeachr.subjectToTeach = courses.map((subject) => subject.id);
        }

        // Save address if changed
        let addressId = clonedTeachr?.address?.id ?? teachr?.address?.id;
        if (pendingAddress) {
          let savedAddress: Address;
          if (existingAddress?.id) {
            const toUpdate: Address = {
              ...existingAddress,
              googlePlaceId: pendingAddress.googlePlaceId,
              streetNumber: pendingAddress.streetNumber,
              streetName: pendingAddress.streetName,
              city: pendingAddress.city,
              department: pendingAddress.department,
              region: pendingAddress.region,
              country: pendingAddress.country,
              zip: pendingAddress.zip,
              latitude: pendingAddress.latitude,
              longitude: pendingAddress.longitude,
              formattedAddress: pendingAddress.formattedAddress,
              sessionToken: "",
            };
            toUpdate.extraInformation = "";
            toUpdate.houseCode = "";
            toUpdate.intercom = "";
            savedAddress = await AddressRepository.updateAddress(toUpdate);
          } else {
            savedAddress = await AddressRepository.createAddress(pendingAddress);
          }
          addressId = savedAddress.id;
          clonedTeachr.address = savedAddress;
        }

        if (description && clonedTeachr.id) {
          const body = new ProfileTeachr(
            clonedTeachr.RCS ?? "",
            clonedTeachr.description,
            addressId,
            clonedTeachr.iban ?? "",
            clonedTeachr?.businessType ?? "",
            clonedTeachr.bic ?? "",
            clonedTeachr.tauxTva ?? "0",
            clonedTeachr.tvaNumber ?? ""
          )

          body.subjectToTeach = isBrandActive(ENV_VERIFIED_TRAINER) ? [108] : clonedTeachr.subjectToTeach;
          if (image && image.id) {
            body.profilePicture = image.id;
          }

          const response = await Teachr_Repository.updateProfilTeachr(clonedTeachr.id, body)
          if (response) {
            clonedTeachr.description = response.description;
            clonedTeachr.address = response.address
            clonedTeachr.subjectToTeach = response.subjectToTeach
            // On garde l'objet Image complet (avec contentUrl) quand une photo
            // vient d'être uploadée : la réponse du PUT peut ne renvoyer qu'une
            // référence, ce qui casserait l'affichage de l'avatar.
            clonedTeachr.profilePicture = image ?? response.profilePicture

            const serializedTeachr = JSON.parse(JSON.stringify(clonedTeachr));
            if (serializedTeachr) {
              dispatch(userTeachrSetAccountAction(serializedTeachr));
              dispatch({ type: "SET_TEACHR_INFO", payload: { teachr: serializedTeachr }, });
              setPendingAddress(null);
              setIsEditing(false);
              refetch();
              setReTry(true)
            }
          }
        } else {
          setLoad(false)
          setIsEditing(false);
        }
      } catch (error) {
        setLoad(false)
        console.error("Error during update:", JSON.parse(JSON.stringify(error)));
      } finally {
        setLoad(false)
        setReTry(false)
      }
    }
  };
  const handleSave = () => {
    updateTeachr();
  };

  const handleCancel = () => {
    setDescription(teachr?.description ?? null);
    setCourses(subject ?? []);
    setPendingAddress(null);
    setAddressError(false);
    setErrorMsg({ description: "", courses: "" });
    setIsEditing(false);
  };

  const settingsStyle = useSettingsStyle();
  const ageDisplay = teachr?.birthday ? `${moment().diff(moment(teachr.birthday), "years")} ans` : undefined;

  const profileInfo = [
    {
      id: 5,
      label: "Prénom",
      value: capitalizeFirstLetter(teachr?.firstName),
      icon: <PersonIcon className="w-5 h-5" />,
      type: "firstname" as const,
      edit: false,
      class: '',
      errorMsg: ""
    },
    {
      id: 6,
      label: "Nom",
      value: capitalizeFirstLetter(teachr?.lastName),
      icon: <PersonIcon className="w-5 h-5" />,
      type: "lastname" as const,
      errorMsg: ""
    },
    {
      id: 1,
      label: "Email",
      value: teachr?.mail,
      icon: <EmailTwoToneIcon className="w-5 h-5" />,
      type: "email" as const,
      edit: false,
      class: '',
      errorMsg: ""
    },
    {
      id: 2,
      label: "Téléphone",
      value: formatPhoneNumber(teachr?.phone as string),
      icon: <LocalPhoneTwoToneIcon className="w-5 h-5" />,
      type: "phone" as const,
      errorMsg: ""
    },
    {
      id: 3,
      label: "Date de naissance",
      value: moment(teachr?.birthday).format("DD MMMM YYYY"),
      icon: <CakeTwoToneIcon className="w-5 h-5" />,
      type: "birthday" as const,
      edit: false,
      class: '',
      errorMsg: ""
    },
    {
      id: 4,
      label: "Civilité",
      value: teachr?.sex === "MAN" ? "Homme" : "Femme",
      icon: <PersonIcon className="w-5 h-5" />,
      type: "gender" as const,
      edit: false,
      class: '',
      errorMsg: ""
    },
  ];

  return (
    <Settings title="Informations générales">
      <Grid item xs={12}>
        {settingsStyle === "v2" ? (
          // Paramètres repensés (maquette 8a) : en-tête compact, photo, nom, âge, ancienneté, e-mail
          <ProfileHeaderV2 teachr={teachr} onImageUpload={getFile} loading={profileLoading} errorMessage={erroImage || undefined} />
        ) : (
        <PhotoSection
          teachr={teachr}
          // profileImageUrl={image}
          firstName={capitalizeFirstLetter(teachr?.firstName)}
          lastName={capitalizeFirstLetter(teachr?.lastName)}
          age={ageDisplay}
          email={teachr?.mail ?? undefined}
          memberSinceYear={teachr?.creationDatetime ? moment(teachr.creationDatetime).format("YYYY") : undefined}
          isEditing={isEditing}
          onImageUpload={getFile}
          loading={profileLoading}
          errorMessage={erroImage || undefined}
          badgeStats={{
            students: 0,
            declaredHours: 0,
            consecutiveMonths: 0,
            courseVolume: 0,
            sponsoredTeachers: Array.isArray(dataSponsor) ? dataSponsor.length : 0,
            vaFromSponsored: 0,
            declaronsSponsored: 0,
          }}
          disableGridItem
        />
        )}
      </Grid>
      <Grid item xs={12}>
        <SettingsSection
          title="Mon profil"
          subtitle="Gérez les informations de votre compte, vos préférences et vos paramètres de confidentialité."
          isEditing={isEditing}
          onEditClick={() => setIsEditing(true)}
          onSave={handleSave}
          onCancel={handleCancel}
          loading={load}
        >
          {profileInfo.map((info, index) => (
            <Grid key={index} item xs={12} sm={12} md={12} lg={6}>
              <FormInput
                label={info.label}
                type="text"
                placeholder=""
                value={info.value ?? ""}
                readOnly
                onChange={() => { }}
                className="cursor-not-allowed"
              />
            </Grid>
          ))}

          {!isBrandActive(ENV_VERIFIED_TRAINER) && (
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <FormSubjectSelect
                label={`Mes ${getActivePrestationBrandConfig()}`}
                placeholder={isBrandActive(ENV_DECLARONS) ? "Jardinage, travaux de bricolages..." : "SVT, piano, anglais..."}
                error={errorMsg.courses || undefined}
                callBack={getCourses}
                defaultValue={subject ?? undefined}
                readOnly={!isEditing}
                customClass="border-blue-400"
                isEditing={isEditing}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            {isEditing ? (
              <InputAddressField
                label="Adresse postale"
                placeholder="Ex : 5 rue de la Seine, Paris 75006"
                default={currentFormatted}
                getPlaceId={getAddress}
                error={addressError ? "Merci de renseigner une adresse" : undefined}
                isEditing={isEditing}
              />
            ) : formattedAddress ? (
              <div
                className="flex items-start gap-3 rounded-xl p-4 border"
                style={{
                  backgroundColor: `${brandConfig.colors?.primary ?? "#16a34a"}08`,
                  borderColor: `${brandConfig.colors?.primary ?? "#16a34a"}30`,
                }}
              >
                <LocationOnIcon
                  sx={{
                    color: brandConfig.colors?.primary ?? "#16a34a",
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                />
                <div>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontFamily={brandConfig.fontFamily}
                    display="block"
                  >
                    Adresse principale
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    fontFamily={brandConfig.fontFamily}
                  >
                    {formattedAddress}
                  </Typography>
                </div>
                {existingAddress && (
                  <Chip
                    label="Par défaut"
                    size="small"
                    sx={{
                      ml: "auto",
                      bgcolor: `${brandConfig.colors?.primary ?? "#16a34a"}18`,
                      color: brandConfig.colors?.primary ?? "#16a34a",
                      fontWeight: 600,
                      fontFamily: brandConfig.fontFamily,
                    }}
                  />
                )}
              </div>
            ) : (
              <Alert severity="info" sx={{ fontFamily: brandConfig.fontFamily }}>
                Aucune adresse renseignée. Cliquez sur "✏️Modifier" pour en ajouter
                une.
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <FormInput
              label={"Ma description"}
              type="text"
              readOnly={!isEditing}
              value={description ?? ""}
              onChangeText={(e) => setDescription(e.target.value)}
              textarea={true}
              placeholder="Expliquez ici à l'élève ou parent pourquoi il devrait retenir votre candidature. Pensez à soigner l'orthographe 😉"
              isEditing={isEditing}
              error={errorMsg.description || undefined}
            />
          </Grid>

        </SettingsSection>
      </Grid>
    </Settings>
  );
};

export default ProfilStep;


